import type { TripDetailsV2 } from "@b2bportal/air-shopping-api";
import {
  PackageEndpointsV2,
  BundleResponseEnum,
  type PackagesCustomizeTripDetailsRequest,
} from "@b2bportal/core-packages";
import { axiosInstance } from "@hopper-b2b/api";
import { flightsShoppingApi, flightsExchangeApi } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import { PackageFlightSelectors } from "@/store/packages/selectors";
import { isExchangeFlow } from "@/utilities/flights/isExchangeFlow";

interface FetchTripDetailsAction {
  tripId: string;
  fareId?: string;
}

export const fetchTripDetails = createAsyncThunk<
  TripDetailsV2,
  FetchTripDetailsAction,
  { state: RootState }
>(
  "flightsShop/fetchTripDetails",
  async (action: FetchTripDetailsAction, thunkAPI) => {
    try {
      const state = thunkAPI.getState();

      // instead of currPackageToken, we should use the packageToken from the customize response,
      // get the token from the trip id?
      const relatedPackageToken =
        PackageFlightSelectors.getRelatedPackageTokenByFareId(
          state,
          action.fareId
        );
      if (relatedPackageToken?.value) {
        const tripDetailsRequest: PackagesCustomizeTripDetailsRequest = {
          customize: {
            bundledPackageStateToken: { value: relatedPackageToken.value },
            flightTripDetailsRequest: {
              tripId: action.tripId,
            },
          },
        };

        const { data } = await axiosInstance.post(
          PackageEndpointsV2.Customize,
          tripDetailsRequest
        );
        if (
          data.BundleResponse === BundleResponseEnum.FlightTripDetailsResponse
        ) {
          const { result, packageStateByFareId } = data.flightTripDetails;
          // Send package state by fare id to the store
          // thunkAPI.dispatch(PackagesActions.setPackageStateByFareId(packageStateByFareId));
          return result;
        }
      }

      // Use exchange-specific endpoint if in exchange flow
      if (isExchangeFlow()) {
        const res =
          await flightsExchangeApi.apiV0SelfserveFlightTripDetailsPost({
            value: action.tripId,
          });
        return res.data;
      }

      // Not in packages or exchange, continue with regular shop endpoint.
      const res = await flightsShoppingApi.apiV0TripDetailPost({
        value: action.tripId,
      });
      return res.data;
    } catch (error) {
      // Reject the thunk with an error message
      return thunkAPI.rejectWithValue(error);
    }
  }
);
